import { FileTextIcon } from '@phosphor-icons/react';

import { Card } from '../../../components/Card';
import { Pill } from '../../../components/pill/Pill';
import { useTranslation } from '../../../contexts/translation-context';
import { OrderDocumentType } from 'src/types/enums';
import {
  OrderDocumentTypeToTranslationKey,
  RequiredDocument,
  RequiredDocumentStatusToColor,
  RequiredDocumentStatusToTranslationKey,
  isCarrierUploadableDocumentType,
} from '../../order/utils/document-validation';
import { CarrierCmrUploaderButton } from './CarrierCmrUploaderButton';

interface Props {
  carrierId: number;
  orderId: string;
  /** Full checklist as returned by the carrier portal order endpoint. */
  requiredDocuments: RequiredDocument[];
  /**
   * Whether the order actually has documents overdue. Separate from the
   * checklist because the server suppresses nagging on brand-new orders and on
   * orders a planner pinned manually — the list still shows, it just doesn't
   * shout.
   */
  hasOutstanding?: boolean;
  disableUpload?: boolean;
  onUploaded: (type: OrderDocumentType) => Promise<void>;
  className?: string;
}

/**
 * The answer to "which documents do I have to upload for this order?" — the
 * customer's required-document list with the state of each one. Always visible,
 * unlike the outstanding-documents banner, which only nags once something is
 * actually overdue.
 */
export const CarrierPortalRequiredDocsCard: React.FC<Props> = ({
  carrierId,
  orderId,
  requiredDocuments,
  hasOutstanding,
  disableUpload,
  onUploaded,
  className,
}) => {
  const { i18n } = useTranslation();

  if (!requiredDocuments.length) return null;

  const satisfiedCount = requiredDocuments.filter((d) => d.status === 'satisfied').length;

  return (
    <Card
      flush
      className={className}
      title={i18n('carrierPortal.orders.requiredDocsTitle')}
      subtitle={i18n('carrierPortal.orders.requiredDocsSubtitle')}
      rightHeading={
        <Pill color={satisfiedCount === requiredDocuments.length ? 'positive' : hasOutstanding ? 'warning' : 'neutral'}>
          {i18n('carrierPortal.orders.requiredDocsProgress', {
            done: String(satisfiedCount),
            total: String(requiredDocuments.length),
          })}
        </Pill>
      }
    >
      <div>
        {requiredDocuments.map((doc) => {
          // Pending is deliberately not actionable: a second upload while the
          // first is still being validated only creates duplicate work.
          const canUpload =
            isCarrierUploadableDocumentType(doc.type) && doc.status !== 'satisfied' && doc.status !== 'pending';

          return (
            <div
              key={doc.type}
              className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-3.5 last:border-b-0"
            >
              <FileTextIcon className="h-[15px] w-[15px] shrink-0 text-fg-muted" />
              <div className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">
                {i18n(OrderDocumentTypeToTranslationKey[doc.type])}
                {doc.count && ` ${doc.count.received}/${doc.count.required}`}
              </div>
              <Pill color={RequiredDocumentStatusToColor[doc.status]}>
                {i18n(RequiredDocumentStatusToTranslationKey[doc.status])}
              </Pill>
              {canUpload && (
                <CarrierCmrUploaderButton
                  carrierId={carrierId}
                  orderId={orderId}
                  type={doc.type}
                  size={8}
                  buttonLabel={
                    doc.status === 'rejected'
                      ? i18n('carrierPortal.orders.requiredDocReupload')
                      : i18n('carrierPortal.orders.uploadRequiredDoc')
                  }
                  disableUpload={disableUpload}
                  onUploaded={onUploaded}
                />
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};
